import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  AlertTriangle, TrendingUp, Shield, ArrowRight, Loader2, Plus,
  BarChart3, FileText, Download, Lock, Check, 
  IndianRupee, Target, Users, Zap, ThumbsUp, ThumbsDown
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { api, initiatePayment } from '@/lib/api';
import { toast } from 'sonner';
import { SiteFooter } from '@/components/SiteFooter';
import { ShareReportBar } from '@/components/ShareButtons';

// ─── Color System ───
const SCORE_RING = { Strong: '#16a34a', Viable: '#2563eb', Fragile: '#d97706', 'High Risk': '#ea580c', 'Not Investable': '#dc2626' };
const SCORE_BG = { Strong: 'bg-emerald-50 border-emerald-200', Viable: 'bg-blue-50 border-blue-200', Fragile: 'bg-amber-50 border-amber-200', 'High Risk': 'bg-orange-50 border-orange-200', 'Not Investable': 'bg-red-50 border-red-200' };
const SCORE_TEXT = { Strong: 'text-emerald-700', Viable: 'text-blue-700', Fragile: 'text-amber-700', 'High Risk': 'text-orange-700', 'Not Investable': 'text-red-700' };
const SEV_STYLE = { critical: 'bg-red-50 border-red-200 text-red-800', major: 'bg-orange-50 border-orange-200 text-orange-800', warning: 'bg-amber-50 border-amber-200 text-amber-800' };
const COST_COLORS = ['#1D1D1F', '#4A4A4F', '#6e6e73', '#86868B', '#a1a1a6', '#c7c7cc', '#d1d1d6'];

function ScoreGauge({ score, label, size = 180 }) {
  const pct = Math.min(score, 100);
  const r = (size - 16) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  const color = SCORE_RING[label] || '#86868B';
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F5F5F7" strokeWidth="10" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.2s ease' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl md:text-5xl font-bold text-[#1D1D1F]">{score}</span>
        <span className="text-xs text-[#1D1D1F]/50 font-semibold mt-0.5">of 100</span>
      </div>
    </div>
  );
}

function ScoreBar({ label, score, max, icon: Icon }) {
  const pct = max > 0 ? (score / max) * 100 : 0;
  const color = pct >= 70 ? 'bg-emerald-500' : pct >= 45 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-3">
      {Icon && <Icon className="h-4 w-4 text-[#1D1D1F]/40 flex-shrink-0" strokeWidth={1.5} />}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-baseline mb-1">
          <span className="text-xs font-semibold text-[#1D1D1F]">{label}</span>
          <span className="text-xs font-bold text-[#1D1D1F]">{score}<span className="text-[#1D1D1F]/30">/{max}</span></span>
        </div>
        <div className="h-2 bg-[#F5F5F7] rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-1000 ${color}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, positive }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5E5EA] p-4 md:p-5">
      <div className="text-2xl md:text-3xl font-bold text-[#1D1D1F] tracking-tight">{value}</div>
      <div className="text-xs font-semibold text-[#1D1D1F]/50 mt-1">{label}</div>
      {sub && <div className={`text-[10px] mt-1.5 font-medium ${positive === true ? 'text-emerald-600' : positive === false ? 'text-red-500' : 'text-[#1D1D1F]/40'}`}>{sub}</div>}
    </div>
  );
}

function CostDonut({ financials }) {
  const revenue = financials?.monthly_revenue || 1;
  const grossRev = financials?.gross_revenue || revenue;
  const items = [
    { name: 'Food Cost', value: financials?.food_cost || 0 },
    { name: 'Staff', value: financials?.salary_cost || 0 },
    { name: 'Rent', value: financials?.rent || 0 },
    { name: 'Packaging', value: financials?.packaging_cost || 0 },
    { name: 'Electricity', value: financials?.electricity || 0 },
    { name: 'Marketing', value: financials?.marketing || 0 },
    { name: 'Misc (10%)', value: financials?.misc_cost || 0 },
    { name: `GST (${financials?.gst_rate || 5}%)`, value: financials?.gst_amount || 0 },
  ].filter(i => i.value > 0);
  return (
    <div className="flex flex-col md:flex-row items-center gap-6">
      <div className="w-44 h-44 flex-shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={items} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={72} paddingAngle={2} strokeWidth={0}>
              {items.map((_, i) => <Cell key={i} fill={COST_COLORS[i % COST_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex-1 space-y-2 w-full">
        <p className="text-[10px] text-[#1D1D1F]/40 font-semibold uppercase tracking-wider mb-2">% of Gross Revenue</p>
        {items.map((item, i) => (
          <div key={i} className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COST_COLORS[i % COST_COLORS.length] }} />
              <span className="font-medium text-[#1D1D1F]">{item.name}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[#1D1D1F]/50">{Math.round(item.value / grossRev * 100)}%</span>
              <span className="font-semibold text-[#1D1D1F] w-16 text-right">₹{(item.value/1000).toFixed(0)}K</span>
            </div>
          </div>
        ))}
        <div className="flex items-center justify-between text-xs pt-2 border-t border-[#F5F5F7] mt-2">
          <span className="font-bold text-[#1D1D1F]">Final Profit</span>
          <div className="flex items-center gap-3">
            <span className={`font-bold ${(financials?.net_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {Math.round((financials?.net_profit || 0) / grossRev * 100)}%
            </span>
            <span className={`font-bold w-16 text-right ${(financials?.net_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              ₹{((financials?.net_profit || 0)/1000).toFixed(0)}K
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const fc = (val) => {
  if (!val && val !== 0) return '₹0';
  const abs = Math.abs(val), sign = val < 0 ? '-' : '';
  if (abs >= 100000) return `${sign}₹${(abs/100000).toFixed(1)}L`;
  if (abs >= 1000) return `${sign}₹${(abs/1000).toFixed(0)}K`;
  return `${sign}₹${abs}`;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function FeedbackSection({ reportId }) {
  const [submitted, setSubmitted] = useState(false);
  const [comment, setComment] = useState('');
  const [showComment, setShowComment] = useState(false);
  const [showGrievance, setShowGrievance] = useState(false);
  const [grievanceDesc, setGrievanceDesc] = useState('');
  const [grievanceType, setGrievanceType] = useState('error');
  const [grievancePhoto, setGrievancePhoto] = useState(null);
  const [grievanceLoading, setGrievanceLoading] = useState(false);

  const submit = async (rating) => {
    try {
      const token = localStorage.getItem('session_token');
      await fetch(`${BACKEND_URL}/api/feedback`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ report_id: reportId, rating, comment }),
      });
      setSubmitted(true);
      toast.success('Thanks for your feedback');
    } catch { /* ignore */ }
  };

  const submitGrievance = async () => {
    setGrievanceLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const formData = new FormData();
      formData.append('report_id', reportId);
      formData.append('issue_type', grievanceType);
      formData.append('description', grievanceDesc);
      if (grievancePhoto) formData.append('photo', grievancePhoto);

      await fetch(`${BACKEND_URL}/api/grievances/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      setShowGrievance(false);
      toast.success('Issue reported. We will review and respond.');
    } catch {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setGrievanceLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-6 mb-6">
        <p className="text-xs text-[#1D1D1F]/40 font-medium">Thanks for your feedback. It helps us improve accuracy.</p>
      </div>
    );
  }

  return (
    <div className="py-6 mb-6" data-testid="feedback-section">
      <div className="text-center">
        <p className="text-xs font-semibold text-[#1D1D1F]/50 mb-3">Was this report accurate and helpful?</p>
        <div className="flex justify-center gap-3 mb-3 flex-wrap">
          <button data-testid="feedback-positive" onClick={() => !showComment ? submit('positive') : null} onMouseDown={() => setShowComment(false)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E5E5EA] text-xs font-medium text-[#1D1D1F] hover:bg-emerald-50 hover:border-emerald-200 transition-colors">
            <ThumbsUp className="h-3.5 w-3.5" strokeWidth={1.5} /> Yes, accurate
          </button>
          <button data-testid="feedback-negative" onClick={() => setShowComment(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E5E5EA] text-xs font-medium text-[#1D1D1F] hover:bg-red-50 hover:border-red-200 transition-colors">
            <ThumbsDown className="h-3.5 w-3.5" strokeWidth={1.5} /> Needs improvement
          </button>
          <button data-testid="report-issue-btn" onClick={() => setShowGrievance(!showGrievance)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E5E5EA] text-xs font-medium text-[#1D1D1F] hover:bg-amber-50 hover:border-amber-200 transition-colors">
            <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} /> Report an Issue
          </button>
        </div>

        {showComment && (
          <div className="max-w-sm mx-auto space-y-2 mb-4">
            <textarea data-testid="feedback-comment" value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder="What felt inaccurate? (optional)"
              className="w-full bg-white border border-[#E5E5EA] rounded-xl p-3 text-xs text-[#1D1D1F] resize-none h-20 focus:outline-none focus:ring-1 focus:ring-[#0071E3]" />
            <button data-testid="feedback-submit" onClick={() => submit('negative')}
              className="px-5 py-2 rounded-full bg-[#1D1D1F] text-white text-xs font-medium">Submit Feedback</button>
          </div>
        )}
      </div>

      {/* Grievance Form */}
      {showGrievance && (
        <div className="max-w-md mx-auto mt-4 bg-white rounded-2xl border border-[#E5E5EA] p-5 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-testid="grievance-form">
          <h4 className="text-sm font-bold text-[#1D1D1F] mb-3">Report an Issue</h4>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-bold text-[#1D1D1F]/50 block mb-1">Issue Type</label>
              <select data-testid="grievance-type" value={grievanceType} onChange={e => setGrievanceType(e.target.value)}
                className="w-full bg-[#F5F5F7] border border-[#E5E5EA] rounded-xl px-3 py-2 text-xs text-[#1D1D1F] focus:outline-none focus:ring-1 focus:ring-[#0071E3]">
                <option value="error">Calculation Error</option>
                <option value="inaccuracy">Scoring Inaccuracy</option>
                <option value="missing">Missing Information</option>
                <option value="ui">Display / UI Issue</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold text-[#1D1D1F]/50 block mb-1">Describe the Issue</label>
              <textarea data-testid="grievance-description" value={grievanceDesc} onChange={e => setGrievanceDesc(e.target.value)}
                placeholder="Explain what seems incorrect or needs attention..."
                className="w-full bg-[#F5F5F7] border border-[#E5E5EA] rounded-xl p-3 text-xs text-[#1D1D1F] resize-none h-24 focus:outline-none focus:ring-1 focus:ring-[#0071E3]" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-[#1D1D1F]/50 block mb-1">Attach Screenshot (optional)</label>
              <input data-testid="grievance-photo" type="file" accept="image/*" onChange={e => setGrievancePhoto(e.target.files?.[0] || null)}
                className="w-full text-xs text-[#1D1D1F]/60 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-[10px] file:font-semibold file:bg-[#1D1D1F] file:text-white hover:file:bg-[#434344]" />
              {grievancePhoto && <p className="text-[10px] text-emerald-600 mt-1">{grievancePhoto.name} attached</p>}
            </div>
            <button data-testid="grievance-submit" onClick={submitGrievance} disabled={grievanceLoading || !grievanceDesc.trim()}
              className="w-full px-5 py-2.5 rounded-full bg-[#1D1D1F] text-white text-xs font-semibold disabled:opacity-50">
              {grievanceLoading ? 'Submitting...' : 'Submit Issue Report'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Report() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPaid, setIsPaid] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [showScenarioForm, setShowScenarioForm] = useState(false);
  const [scenarioForm, setScenarioForm] = useState({ rent: '', aov: '', staff_count: '', food_cost_pct: '' });
  const [scenarioLabel, setScenarioLabel] = useState('');
  const [scenarioLoading, setScenarioLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);

  useEffect(() => {
    if (!user) { login(); return; }
    loadReport();
    loadScenarios();
  }, [reportId, user]); // eslint-disable-line

  const loadReport = async () => {
    try {
      const d = await api.unlockReport(reportId);
      setReport(d); setIsPaid(true);
    } catch {
      try { const d = await api.getReport(reportId); setReport(d); setIsPaid(d.plan && d.plan !== 'free'); } catch { navigate('/dashboard'); }
    } finally { setLoading(false); }
  };
  const loadScenarios = async () => { setScenarios(await api.getScenarios(reportId)); };
  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      await api.downloadPdf(reportId);
    } catch (err) {
      toast.error('Download failed. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };
  const createScenario = async () => {
    setScenarioLoading(true);
    try {
      const mods = {};
      if (scenarioForm.rent) mods.rent = Number(scenarioForm.rent);
      if (scenarioForm.aov) mods.aov = Number(scenarioForm.aov);
      if (scenarioForm.staff_count) mods.staff_count = Number(scenarioForm.staff_count);
      if (scenarioForm.food_cost_pct) mods.food_cost_pct = Number(scenarioForm.food_cost_pct);
      await api.createScenario(reportId, scenarioLabel || `Scenario ${scenarios.length + 1}`, mods);
      await loadScenarios();
      setShowScenarioForm(false); setScenarioForm({ rent: '', aov: '', staff_count: '', food_cost_pct: '' }); setScenarioLabel('');
    } catch { toast.error('No scenario credits remaining'); }
    finally { setScenarioLoading(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#FBFBFD] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#86868B]" /></div>;
  if (!report) return null;

  const { score, model_label, financials, risks, confidence, execution_dependency, llm_explanation } = report;
  const isAdmin = user?.email === 'rohit.chidurala@gmail.com';
  const canAccessPremium = isPaid || isAdmin;
  const reportPlan = report.plan || 'basic';
  const scenariosAllowed = report.scenarios_allowed !== false && (reportPlan === 'scenario_pack' || reportPlan === 'pro' || isAdmin);
  const isProPlan = reportPlan === 'pro' || isAdmin;
  const handleUpgrade = (plan) => { if (isAdmin) return; initiatePayment(plan, reportId, user, () => window.location.reload()); };
  const handleConsultation = (type) => { if (isAdmin) { navigate('/consultation', { state: { type, reportId } }); return; } initiatePayment(type, reportId, user, () => navigate('/consultation', { state: { type, reportId } })); };

  const marginInsight = financials?.operating_margin >= 20 ? 'Healthy margin' : financials?.operating_margin >= 10 ? 'Thin but workable' : financials?.operating_margin >= 0 ? 'Dangerously thin' : 'Loss-making';
  const runwayInsight = financials?.runway_months >= 999 ? 'Profitable from day 1' : financials?.runway_months >= 6 ? 'Good buffer' : financials?.runway_months >= 3 ? 'Tight runway' : 'Critical — will run out fast';
  const beInsight = financials?.breakeven_daily_orders && report.inputs?.daily_orders ? (financials.breakeven_daily_orders / report.inputs.daily_orders < 0.6 ? 'Comfortable target' : financials.breakeven_daily_orders / report.inputs.daily_orders < 0.8 ? 'Achievable but tight' : 'Very hard to hit') : '';
  const roiMonths = financials?.roi_breakeven_months;
  const roiInsight = roiMonths === -1 ? 'Not profitable — no ROI' : roiMonths <= 18 ? 'Fast payback' : roiMonths <= 30 ? 'Reasonable payback' : roiMonths <= 48 ? 'Slow payback — high risk' : 'Very slow — reconsider';
  const roiPositive = roiMonths > 0 && roiMonths <= 30;

  return (
    <div className="min-h-screen bg-[#FBFBFD]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-[#E5E5EA]">
        <div className="max-w-5xl mx-auto px-4 md:px-8 flex items-center justify-between h-14">
          <button onClick={() => navigate('/')}><img src="https://customer-assets.emergentagent.com/job_unit-economics-3/artifacts/z3ko7m68_Cake_Studio.png" alt="ROCH" className="h-14 w-auto" /></button>
          <div className="flex items-center gap-2">
            {canAccessPremium && (
              <Button data-testid="download-pdf-btn" onClick={handleDownloadPdf} disabled={pdfLoading} variant="ghost" size="sm" className="text-[#1D1D1F]/60 text-xs rounded-full">
                {pdfLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" strokeWidth={1.5} />}
                <span className="ml-1">{pdfLoading ? 'Preparing...' : 'Download Report'}</span>
              </Button>
            )}
            <Button data-testid="go-dashboard" onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="text-[#1D1D1F]/60 text-xs rounded-full">Dashboard</Button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12">

        {/* ─── 1. HERO SCORE ─── */}
        <section className="text-center mb-10" data-testid="score-hero">
          <div className="inline-block mb-4">
            <Badge className={`text-xs px-3 py-1 rounded-full border font-semibold ${SCORE_BG[score?.label]} ${SCORE_TEXT[score?.label]}`}>
              {model_label} &middot; {report.inputs?.city}
            </Badge>
          </div>
          <div className="flex justify-center mb-5">
            <ScoreGauge score={score?.total || 0} label={score?.label} />
          </div>
          <h1 className={`text-2xl md:text-3xl font-bold tracking-tight mb-2 ${SCORE_TEXT[score?.label]}`} data-testid="score-label">
            {score?.label}
          </h1>
          <p className="text-sm text-[#1D1D1F]/60 font-medium max-w-md mx-auto mb-4">
            {score?.total >= 68 ? 'This concept has potential, but review the details below carefully.' :
             score?.total >= 52 ? 'Significant concerns exist. This needs rework before investing.' :
             score?.total >= 38 ? 'This is high-risk. Most of the numbers don\'t work yet.' :
             'This concept is not viable at current numbers. Rethink fundamentals.'}
          </p>
          <div className="flex justify-center gap-3 flex-wrap">
            <Badge variant="outline" className={`text-[10px] rounded-full px-3 py-1 font-semibold ${
              confidence?.level === 'high' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' :
              confidence?.level === 'medium' ? 'border-amber-300 text-amber-700 bg-amber-50' :
              'border-red-300 text-red-700 bg-red-50'
            }`}>
              Data Confidence: {confidence?.level?.toUpperCase()}
            </Badge>
            <Badge variant="outline" className={`text-[10px] rounded-full px-3 py-1 font-semibold ${
              execution_dependency?.level === 'minimal' || execution_dependency?.level === 'low' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' :
              execution_dependency?.level === 'medium' ? 'border-amber-300 text-amber-700 bg-amber-50' :
              'border-red-300 text-red-700 bg-red-50'
            }`}>
              Execution Difficulty: {execution_dependency?.level?.toUpperCase()}
            </Badge>
          </div>
        </section>

        {/* ─── 2. SCORE BREAKDOWN ─── */}
        <section className="bg-white rounded-3xl border border-[#E5E5EA] p-5 md:p-8 mb-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-testid="score-breakdown">
          <h3 className="text-sm font-bold text-[#1D1D1F] mb-5">Score Breakdown</h3>
          <div className="space-y-4">
            <ScoreBar label="Concept Fit" score={score?.breakdown?.concept_fit || 0} max={15} icon={Target} />
            <ScoreBar label="Financial Viability" score={score?.breakdown?.financial_viability || 0} max={30} icon={IndianRupee} />
            <ScoreBar label="Unit Economics" score={score?.breakdown?.unit_economics || 0} max={20} icon={BarChart3} />
            <ScoreBar label="Operations" score={score?.breakdown?.operations || 0} max={15} icon={Zap} />
            <ScoreBar label="Founder Readiness" score={score?.breakdown?.founder_readiness || 0} max={10} icon={Users} />
            <ScoreBar label="Risk" score={score?.breakdown?.risk || 0} max={10} icon={Shield} />
          </div>
        </section>

        {/* ─── CONFIDENCE, EXECUTION & LOCATION ─── */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {/* Location Fit */}
          {report.location_fit && (
          <div className={`rounded-2xl border p-5 ${
            report.location_fit.total >= 7 ? 'border-emerald-200 bg-emerald-50/30' :
            report.location_fit.total >= 5 ? 'border-amber-200 bg-amber-50/30' :
            'border-red-200 bg-red-50/30'
          }`} data-testid="location-fit-card">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 opacity-60" strokeWidth={1.5} />
              <h4 className="text-xs font-bold text-[#1D1D1F] uppercase tracking-wide">Location Fit</h4>
              <Badge variant="outline" className={`text-[9px] rounded-full ml-auto font-bold ${
                report.location_fit.total >= 7 ? 'text-emerald-700 border-emerald-300' :
                report.location_fit.total >= 5 ? 'text-amber-700 border-amber-300' :
                'text-red-700 border-red-300'
              }`}>{report.location_fit.total}/10</Badge>
            </div>
            <div className="space-y-1 mb-2">
              <p className="text-[10px] text-[#1D1D1F]/50"><span className="font-semibold text-[#1D1D1F]">Area:</span> {report.location_fit.area_type}</p>
              <p className="text-[10px] text-[#1D1D1F]/50"><span className="font-semibold text-[#1D1D1F]">Target:</span> {report.location_fit.spending_level} — {report.location_fit.audience_type}</p>
            </div>
            {report.location_fit.insights?.slice(0, 2).map((insight, i) => (
              <p key={i} className="text-[10px] text-[#1D1D1F]/60 leading-relaxed">{insight}</p>
            ))}
            {report.area_research?.known_for && (
              <p className="text-[10px] text-[#1D1D1F]/50 mt-1 pt-1 border-t border-[#E5E5EA]">
                <span className="font-semibold">Area insight:</span> {report.area_research.known_for}
              </p>
            )}
            {report.area_research?.seasonality && report.area_research.seasonality !== 'none' && (
              <p className="text-[10px] text-amber-600 font-medium mt-1">Seasonality: {report.area_research.seasonality}</p>
            )}
          </div>
          )}

          <div className={`rounded-2xl border p-5 ${
            confidence?.level === 'high' ? 'border-emerald-200 bg-emerald-50/30' :
            confidence?.level === 'medium' ? 'border-amber-200 bg-amber-50/30' :
            'border-red-200 bg-red-50/30'
          }`} data-testid="confidence-card">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-4 w-4 opacity-60" strokeWidth={1.5} />
              <h4 className="text-xs font-bold text-[#1D1D1F] uppercase tracking-wide">Data Confidence</h4>
              <Badge variant="outline" className={`text-[9px] rounded-full ml-auto font-bold ${
                confidence?.level === 'high' ? 'text-emerald-700 border-emerald-300' :
                confidence?.level === 'medium' ? 'text-amber-700 border-amber-300' :
                'text-red-700 border-red-300'
              }`}>{confidence?.level?.toUpperCase()}</Badge>
            </div>
            <p className="text-xs text-[#1D1D1F]/50 mb-3 font-medium leading-relaxed">
              How realistic and consistent are the numbers you entered? Higher confidence means the score is more reliable.
            </p>
            <p className="text-xs text-[#1D1D1F]/70 font-medium mb-2">{confidence?.summary}</p>
            {confidence?.issues?.length > 0 && (
              <div className="space-y-1 mt-2">
                {confidence.issues.slice(0, 3).map((issue, i) => (
                  <p key={i} className="text-[10px] text-red-600/70 flex items-start gap-1.5">
                    <span className="mt-0.5">!</span> {issue}
                  </p>
                ))}
              </div>
            )}
            {confidence?.strengths?.length > 0 && (
              <div className="space-y-1 mt-2">
                {confidence.strengths.slice(0, 2).map((s, i) => (
                  <p key={i} className="text-[10px] text-emerald-600/70 flex items-start gap-1.5">
                    <Check className="h-3 w-3 mt-0.5 flex-shrink-0" strokeWidth={2} /> {s}
                  </p>
                ))}
              </div>
            )}
          </div>

          <div className={`rounded-2xl border p-5 ${
            execution_dependency?.level === 'minimal' || execution_dependency?.level === 'low' ? 'border-emerald-200 bg-emerald-50/30' :
            execution_dependency?.level === 'medium' ? 'border-amber-200 bg-amber-50/30' :
            'border-red-200 bg-red-50/30'
          }`} data-testid="execution-card">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 opacity-60" strokeWidth={1.5} />
              <h4 className="text-xs font-bold text-[#1D1D1F] uppercase tracking-wide">Execution Difficulty</h4>
              <Badge variant="outline" className={`text-[9px] rounded-full ml-auto font-bold ${
                execution_dependency?.level === 'minimal' || execution_dependency?.level === 'low' ? 'text-emerald-700 border-emerald-300' :
                execution_dependency?.level === 'medium' ? 'text-amber-700 border-amber-300' :
                'text-red-700 border-red-300'
              }`}>{execution_dependency?.level?.toUpperCase()}</Badge>
            </div>
            <p className="text-xs text-[#1D1D1F]/50 mb-3 font-medium leading-relaxed">
              How hard is this to actually run day-to-day? Higher difficulty means you need more experience, systems, and team discipline.
            </p>
            <p className="text-xs text-[#1D1D1F]/70 font-medium mb-2">{execution_dependency?.summary}</p>
            {execution_dependency?.factors?.length > 0 && (
              <div className="space-y-1 mt-2">
                {execution_dependency.factors.slice(0, 4).map((f, i) => (
                  <p key={i} className="text-[10px] text-[#1D1D1F]/50 flex items-start gap-1.5">
                    <span className="text-[#1D1D1F]/30 mt-0.5">-</span> {f}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Competition */}
          {report.competition && report.competition.score > 0 && (
          <div className={`rounded-2xl border p-5 md:col-span-3 ${
            report.competition.score >= 7 ? 'border-red-200 bg-red-50/30' :
            report.competition.score >= 4 ? 'border-amber-200 bg-amber-50/30' :
            'border-emerald-200 bg-emerald-50/30'
          }`} data-testid="competition-card">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="h-4 w-4 opacity-60" strokeWidth={1.5} />
              <h4 className="text-xs font-bold text-[#1D1D1F] uppercase tracking-wide">Competition Analysis</h4>
              <Badge variant="outline" className={`text-[9px] rounded-full ml-auto font-bold ${
                report.competition.score >= 7 ? 'text-red-700 border-red-300' :
                report.competition.score >= 4 ? 'text-amber-700 border-amber-300' :
                'text-emerald-700 border-emerald-300'
              }`}>{report.competition.density?.replace('_', ' ').toUpperCase()}</Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <div className="bg-white/60 rounded-xl p-2.5 text-center">
                <p className="text-lg font-bold text-[#1D1D1F]">~{report.competition.similar_count}</p>
                <p className="text-[9px] text-[#1D1D1F]/40">Similar Restaurants</p>
              </div>
              <div className="bg-white/60 rounded-xl p-2.5 text-center">
                <p className="text-sm font-bold text-[#1D1D1F] capitalize">{report.competition.survival_difficulty?.replace('_', ' ')}</p>
                <p className="text-[9px] text-[#1D1D1F]/40">Survival Difficulty</p>
              </div>
              <div className="bg-white/60 rounded-xl p-2.5 text-center">
                <p className="text-sm font-bold text-[#1D1D1F] capitalize">{report.competition.saturation}</p>
                <p className="text-[9px] text-[#1D1D1F]/40">Cuisine Saturation</p>
              </div>
              <div className="bg-white/60 rounded-xl p-2.5 text-center">
                <p className="text-sm font-bold text-[#1D1D1F] capitalize">{report.competition.differentiation_needed}</p>
                <p className="text-[9px] text-[#1D1D1F]/40">Differentiation Needed</p>
              </div>
            </div>
            {report.competition.top_competitors?.length > 0 && (
              <p className="text-[10px] text-[#1D1D1F]/60 mb-1"><span className="font-semibold text-[#1D1D1F]">Key competitors:</span> {report.competition.top_competitors.join(', ')}</p>
            )}
            {report.competition.market_gap && (
              <p className="text-[10px] text-[#1D1D1F]/60 mb-1"><span className="font-semibold text-[#1D1D1F]">Market gap:</span> {report.competition.market_gap}</p>
            )}
            {report.competition.differentiation_suggestions?.length > 0 && (
              <p className="text-[10px] text-emerald-600 font-medium mt-1">How to stand out: {report.competition.differentiation_suggestions.join('; ')}</p>
            )}
            {report.competition.key_insight && (
              <p className="text-[10px] text-[#1D1D1F]/50 mt-1 italic">{report.competition.key_insight}</p>
            )}
          </div>
          )}
        </div>
        {canAccessPremium ? (
        <>
        <section className="mb-6" data-testid="financial-snapshot">
          <h3 className="text-sm font-bold text-[#1D1D1F] mb-4">Financial Snapshot</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Gross Revenue" value={fc(financials?.gross_revenue)} sub={`Incl. ${financials?.gst_rate || 5}% GST`} />
            <StatCard label="GST Liability" value={fc(financials?.gst_amount)} sub={`${financials?.gst_rate || 5}% (collected & remitted)`} />
            <StatCard label="Net Revenue" value={fc(financials?.monthly_revenue)} sub="After GST deduction" positive={financials?.monthly_revenue > 500000} />
            <StatCard label="Operating Margin" value={`${financials?.operating_margin}%`} sub={marginInsight} positive={financials?.operating_margin >= 15} />
            <StatCard label="Net Profit" value={fc(financials?.net_profit)} sub={financials?.net_profit > 0 ? 'After all costs + GST' : 'Loss after all costs'} positive={financials?.net_profit > 0} />
            <StatCard label="ROI Breakeven" value={roiMonths === -1 ? 'Never' : `${roiMonths} months`} sub={roiInsight} positive={roiPositive} />
            <StatCard label="Annual ROI" value={`${financials?.roi_annual_pct || 0}%`} sub={financials?.roi_annual_pct > 30 ? 'Strong return' : financials?.roi_annual_pct > 15 ? 'Moderate return' : financials?.roi_annual_pct > 0 ? 'Low return' : 'Negative return'} positive={financials?.roi_annual_pct > 15} />
            <StatCard label="Breakeven" value={`${financials?.breakeven_daily_orders} orders/day`} sub={beInsight} positive={financials?.breakeven_daily_orders < (report.inputs?.daily_orders * 0.7)} />
            <StatCard label="Total Investment" value={fc(financials?.total_investment)} />
            <StatCard label="Runway" value={financials?.runway_months >= 999 ? 'Profitable' : `${financials?.runway_months}mo`} sub={runwayInsight} positive={financials?.runway_months >= 999 || financials?.runway_months >= 6} />
          </div>
        </section>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <section className="bg-white rounded-3xl border border-[#E5E5EA] p-5 md:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-testid="cost-breakdown">
            <h3 className="text-sm font-bold text-[#1D1D1F] mb-5">Where Your Money Goes</h3>
            <CostDonut financials={financials} />
          </section>
          <section className="bg-white rounded-3xl border border-[#E5E5EA] p-5 md:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-testid="revenue-vs-expense">
            <h3 className="text-sm font-bold text-[#1D1D1F] mb-5">Revenue vs Expenses</h3>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { name: 'Revenue', value: financials?.monthly_revenue || 0, fill: '#16a34a' },
                  { name: 'Expenses', value: financials?.total_expenses || 0, fill: '#1D1D1F' },
                  { name: 'Profit', value: Math.max(0, financials?.monthly_profit || 0), fill: '#2563eb' },
                ]} layout="vertical" margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F5F5F7" horizontal={false} />
                  <XAxis type="number" tickFormatter={(v) => `${(v/100000).toFixed(0)}L`} tick={{ fontSize: 10, fill: '#86868B' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fontWeight: 600, fill: '#1D1D1F' }} axisLine={false} tickLine={false} width={70} />
                  <Tooltip formatter={(v) => [`₹${(v/100000).toFixed(1)}L`]} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={24}>
                    {[
                      { name: 'Revenue', fill: '#16a34a' },
                      { name: 'Expenses', fill: '#1D1D1F' },
                      { name: 'Profit', fill: financials?.monthly_profit >= 0 ? '#2563eb' : '#dc2626' },
                    ].map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {financials?.monthly_profit < 0 && (
              <p className="text-xs text-red-500 font-semibold mt-3 text-center">Loss of {fc(financials.monthly_profit)}/month</p>
            )}
          </section>
        </div>
        </>
        ) : (
          <div className="relative mb-6" data-testid="financials-locked">
            <div className="filter blur-md pointer-events-none select-none">
              <div className="mb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <StatCard label="Gross Revenue" value="₹XX.XL" />
                  <StatCard label="GST" value="₹XXK" />
                  <StatCard label="Net Revenue" value="₹XX.XL" />
                  <StatCard label="Margin" value="XX%" />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-[#F5F5F7] rounded-3xl h-44" />
                  <div className="bg-[#F5F5F7] rounded-3xl h-44" />
                </div>
              </div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl border border-[#E5E5EA] shadow-xl px-8 py-6 text-center max-w-xs">
                <Lock className="h-6 w-6 text-[#1D1D1F]/30 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm font-bold text-[#1D1D1F] mb-1">Financial Analysis Locked</p>
                <p className="text-xs text-[#1D1D1F]/40 mb-4 leading-relaxed">Exact revenue, profit, costs, breakeven, and charts are available in the full report.</p>
                <Button onClick={() => handleUpgrade('basic')} className="bg-[#1D1D1F] text-white rounded-full px-6 py-2 text-xs font-semibold w-full">Unlock from ₹499</Button>
              </div>
            </div>
          </div>
        )}
        <section className="mb-6" data-testid="risks-section">
          <h3 className="text-sm font-bold text-[#1D1D1F] mb-4">Risk Assessment</h3>
          {(!risks || risks.length === 0) ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center">
              <Check className="h-8 w-8 text-emerald-600 mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-sm font-semibold text-emerald-800">No major risks detected</p>
              <p className="text-xs text-emerald-600 mt-1">Your numbers look healthy across all checks</p>
            </div>
          ) : (
            <div className="space-y-3">
              {risks.slice(0, 4).map((r, i) => (
                <div key={i} className={`rounded-2xl border p-4 ${SEV_STYLE[r.severity]}`}>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" strokeWidth={2} />
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-bold">{r.flag}</span>
                        <span className="text-[10px] uppercase font-bold opacity-60">{r.severity}</span>
                      </div>
                      <p className="text-xs opacity-80">{r.detail}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ─── 6. EXPERT ANALYSIS (PREMIUM) ─── */}
        {canAccessPremium && llm_explanation ? (
          <section className="bg-white rounded-3xl border border-[#E5E5EA] p-5 md:p-8 mb-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-testid="expert-analysis">
            <h3 className="text-sm font-bold text-[#1D1D1F] mb-5">Expert Analysis</h3>
            {/* Render cleaned text as structured paragraphs */}
            <div className="space-y-4">
              {llm_explanation
                .replace(/\*\*/g, '')
                .replace(/##\s*/g, '')
                .replace(/#\s*/g, '')
                .replace(/---/g, '')
                .replace(/--/g, '')
                .split('\n')
                .filter(l => l.trim())
                .map((line, i) => {
                  const trimmed = line.trim();
                  // Section headers (numbered like "1. CONCEPT FIT" or all caps)
                  const isHeader = /^\d+\.\s/.test(trimmed) || (trimmed === trimmed.toUpperCase() && trimmed.length > 3 && trimmed.length < 60);
                  // Bullet points
                  const isBullet = /^[-•]\s/.test(trimmed);
                  if (isHeader) {
                    return <h4 key={i} className="text-xs font-bold text-[#1D1D1F] uppercase tracking-wide mt-2 pt-2 border-t border-[#F5F5F7] first:border-0 first:mt-0 first:pt-0">{trimmed}</h4>;
                  }
                  if (isBullet) {
                    return <p key={i} className="text-xs text-[#1D1D1F]/70 leading-relaxed pl-3">{trimmed}</p>;
                  }
                  return <p key={i} className="text-xs text-[#1D1D1F]/70 leading-relaxed">{trimmed}</p>;
                })
              }
            </div>
          </section>
        ) : !canAccessPremium && (
          <section className="bg-[#F5F5F7] rounded-3xl p-6 md:p-8 mb-6" data-testid="upgrade-prompt">
            <div className="text-center mb-6">
              <Lock className="h-8 w-8 text-[#1D1D1F]/20 mx-auto mb-3" strokeWidth={1.5} />
              <h3 className="text-lg font-bold text-[#1D1D1F] mb-1">Unlock the Full Report</h3>
              <p className="text-xs text-[#1D1D1F]/50 font-medium">Get financial details, AI analysis, fix suggestions, and scenario testing.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { plan: 'basic', name: 'Report', price: '499', tagline: 'Understand your numbers',
                  features: ['Complete financial breakdown', 'All risk flags explained', 'AI fix suggestions', 'PDF download'] },
                { plan: 'scenario_pack', name: 'Report + Scenarios', price: '999', popular: true, tagline: 'Test before you commit',
                  features: ['Everything in Report', '3 what-if scenario tests', 'Side-by-side comparison', 'Best-case numbers'] },
                { plan: 'pro', name: 'Full Analysis', price: '2,999', tagline: 'Investor-ready clarity',
                  features: ['Everything in Scenarios', '5 scenario credits', 'Benchmark comparisons', 'Priority consultation'] },
              ].map((tier) => (
                <div key={tier.plan} className={`bg-white rounded-2xl border p-5 ${tier.popular ? 'border-[#1D1D1F]' : 'border-[#E5E5EA]'}`}>
                  {tier.popular && <span className="text-[9px] font-bold text-white bg-[#1D1D1F] rounded-full px-2.5 py-0.5 mb-2 inline-block">POPULAR</span>}
                  <h4 className="text-sm font-bold text-[#1D1D1F]">{tier.name}</h4>
                  <p className="text-[10px] text-[#1D1D1F]/40 font-medium mb-2">{tier.tagline}</p>
                  <div className="text-2xl font-bold text-[#1D1D1F] mb-3">₹{tier.price}</div>
                  <ul className="space-y-1.5 mb-4">
                    {tier.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[11px] text-[#1D1D1F]/60 font-medium">
                        <Check className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" strokeWidth={2} />{f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    data-testid={`upgrade-${tier.plan}-btn`}
                    onClick={() => handleUpgrade(tier.plan)}
                    className={`w-full rounded-full py-2.5 text-xs font-semibold ${tier.popular ? 'bg-[#1D1D1F] text-white hover:bg-[#434344]' : 'bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E5E5EA]'}`}
                  >
                    Get {tier.name}
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ─── PRO: COST OPTIMIZATION ─── */}
        {report.cost_optimization && (
          <section className="bg-white rounded-3xl border border-emerald-200 p-5 md:p-8 mb-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-testid="cost-optimization">
            <h3 className="text-sm font-bold text-[#1D1D1F] mb-1">Cost Optimization Insights</h3>
            <p className="text-[10px] text-emerald-600 font-semibold mb-4">PRO FEATURE</p>
            <div className="space-y-3">
              {report.cost_optimization.split('\n').filter(l => l.trim()).map((line, i) => {
                const cleaned = line.replace(/[*#]/g, '').trim();
                if (!cleaned) return null;
                const isNumbered = /^\d+[\.\)]/.test(cleaned);
                return (
                  <p key={i} className={`text-xs text-[#1D1D1F]/70 leading-relaxed ${isNumbered ? 'font-medium text-[#1D1D1F]' : 'pl-4'}`}>{cleaned}</p>
                );
              })}
            </div>
          </section>
        )}

        {/* ─── PRO: BENCHMARKS ─── */}
        {report.benchmarks?.available && report.benchmarks?.comparisons?.length > 0 && (
          <section className="bg-white rounded-3xl border border-blue-200 p-5 md:p-8 mb-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-testid="benchmarks">
            <h3 className="text-sm font-bold text-[#1D1D1F] mb-1">Benchmark Comparison</h3>
            <p className="text-[10px] text-blue-600 font-semibold mb-4">PRO FEATURE — Based on {report.benchmarks.total_reports_analyzed} assessments</p>
            <div className="space-y-2">
              {report.benchmarks.comparisons.map((c, i) => (
                <div key={i} className="flex items-center justify-between bg-[#F5F5F7] rounded-xl px-4 py-3">
                  <span className="text-xs font-medium text-[#1D1D1F]">{c.metric}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-[#1D1D1F]/50">Avg: {c.benchmark}{c.unit || ''}</span>
                    <span className="text-xs font-bold text-[#1D1D1F]">You: {c.yours}{c.unit || ''}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.better ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {c.diff > 0 ? '+' : ''}{c.diff}{c.unit || ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ─── 7. SCENARIOS ─── */}
        {scenariosAllowed && canAccessPremium && (
          <section className="bg-white rounded-3xl border border-[#E5E5EA] p-5 md:p-8 mb-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-testid="scenario-section">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-bold text-[#1D1D1F]">Scenarios</h3>
                <span className="text-[10px] font-semibold text-[#1D1D1F]/40 bg-[#F5F5F7] rounded-full px-2.5 py-0.5">
                  {report.scenario_credits ?? '?'} credits left
                </span>
              </div>
              <Button data-testid="new-scenario-btn" onClick={() => setShowScenarioForm(!showScenarioForm)} variant="ghost" size="sm" className="text-xs rounded-full text-[#0071E3]">
                <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> New
              </Button>
            </div>
            {showScenarioForm && (
              <div className="bg-[#F5F5F7] rounded-2xl p-4 mb-5">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div><Label className="text-[10px] text-[#1D1D1F]/50 mb-1 block">Name</Label><Input data-testid="scenario-label-input" value={scenarioLabel} onChange={e => setScenarioLabel(e.target.value)} placeholder="e.g. Lower Rent" className="h-9 text-xs rounded-xl" /></div>
                  <div><Label className="text-[10px] text-[#1D1D1F]/50 mb-1 block">Rent (₹)</Label><Input data-testid="scenario-rent-input" type="number" value={scenarioForm.rent} onChange={e => setScenarioForm(p => ({...p, rent: e.target.value}))} placeholder={`${report.inputs?.rent || 0}`} className="h-9 text-xs rounded-xl" /></div>
                  <div><Label className="text-[10px] text-[#1D1D1F]/50 mb-1 block">AOV (₹)</Label><Input data-testid="scenario-aov-input" type="number" value={scenarioForm.aov} onChange={e => setScenarioForm(p => ({...p, aov: e.target.value}))} placeholder={`${report.inputs?.aov || 0}`} className="h-9 text-xs rounded-xl" /></div>
                  <div><Label className="text-[10px] text-[#1D1D1F]/50 mb-1 block">Food Cost %</Label><Input data-testid="scenario-foodcost-input" type="number" value={scenarioForm.food_cost_pct} onChange={e => setScenarioForm(p => ({...p, food_cost_pct: e.target.value}))} placeholder={`${report.inputs?.food_cost_pct || 0}`} className="h-9 text-xs rounded-xl" /></div>
                </div>
                <Button data-testid="run-scenario-btn" onClick={createScenario} disabled={scenarioLoading} size="sm" className="bg-[#1D1D1F] text-white rounded-full px-5 text-xs">
                  {scenarioLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null} Run Scenario
                </Button>
              </div>
            )}
            {scenarios.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-5 text-[10px] font-bold text-[#1D1D1F]/40 uppercase tracking-wider px-3">
                  <span>Scenario</span><span className="text-right">Score</span><span className="text-right">Profit</span><span className="text-right">Margin</span><span className="text-right">Verdict</span>
                </div>
                <div className="bg-[#F5F5F7] rounded-xl p-3 grid grid-cols-5 text-xs items-center">
                  <span className="font-semibold text-[#1D1D1F]">Original</span>
                  <span className="text-right font-bold">{score?.total}</span>
                  <span className="text-right">{fc(financials?.monthly_profit)}</span>
                  <span className="text-right">{financials?.operating_margin}%</span>
                  <span className="text-right"><Badge className={`text-[9px] rounded-full border ${SCORE_BG[score?.label]} ${SCORE_TEXT[score?.label]}`}>{score?.label}</Badge></span>
                </div>
                {scenarios.map((s, i) => (
                  <div key={i} className="bg-white border border-[#E5E5EA] rounded-xl p-3 grid grid-cols-5 text-xs items-center">
                    <span className="font-semibold text-[#1D1D1F]">{s.label}</span>
                    <span className="text-right font-bold">{s.score?.total}</span>
                    <span className="text-right">{fc(s.financials?.monthly_profit)}</span>
                    <span className="text-right">{s.financials?.operating_margin}%</span>
                    <span className="text-right"><Badge className={`text-[9px] rounded-full border ${SCORE_BG[s.score?.label]} ${SCORE_TEXT[s.score?.label]}`}>{s.score?.label}</Badge></span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Upsell: Scenarios for Basic users */}
        {canAccessPremium && !scenariosAllowed && (
          <section className="bg-[#F5F5F7] rounded-2xl border border-[#E5E5EA] p-5 mb-6 text-center" data-testid="scenario-upsell">
            <h4 className="text-sm font-bold text-[#1D1D1F] mb-1">Want to test different scenarios?</h4>
            <p className="text-xs text-[#1D1D1F]/50 font-medium mb-3">Change rent, AOV, staff, or food cost and compare results side-by-side.</p>
            <Button onClick={() => handleUpgrade('scenario_pack')} className="bg-[#1D1D1F] text-white hover:bg-[#434344] rounded-full px-6 py-2 text-xs font-semibold">
              Upgrade to Scenarios — ₹999
            </Button>
          </section>
        )}

        {/* Upsell: Pro features for non-pro users */}
        {canAccessPremium && !isProPlan && (
          <section className="bg-[#F5F5F7] rounded-2xl border border-[#E5E5EA] p-5 mb-6 text-center" data-testid="pro-upsell">
            <h4 className="text-sm font-bold text-[#1D1D1F] mb-1">Get cost optimization & benchmark insights</h4>
            <p className="text-xs text-[#1D1D1F]/50 font-medium mb-3">See how your numbers compare and get specific savings recommendations.</p>
            <Button onClick={() => handleUpgrade('pro')} className="bg-[#1D1D1F] text-white hover:bg-[#434344] rounded-full px-6 py-2 text-xs font-semibold">
              Upgrade to Full Analysis — ₹2,999
            </Button>
          </section>
        )}

        {/* ─── 9. CONSULTATION ─── Only for paid users */}
        {canAccessPremium && (
          <section className="bg-[#F5F5F7] rounded-3xl p-6 md:p-8 mb-6" data-testid="consultation-upsell">
            <p className="text-xs font-bold text-[#1D1D1F]/50 uppercase tracking-widest mb-2">Before You Invest</p>
            <h3 className="text-lg font-bold text-[#1D1D1F] mb-1">Get a structured review of your plan.</h3>
            <p className="text-xs text-[#1D1D1F]/50 mb-5 font-medium">You've seen the numbers. Now get expert direction before committing capital.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className={`bg-white rounded-2xl border p-5 ${isProPlan ? 'border-emerald-300' : 'border-[#E5E5EA]'}`}>
                {isProPlan && <p className="text-[9px] font-bold text-white bg-emerald-600 rounded-full px-2.5 py-0.5 mb-3 inline-block">PRIORITY ACCESS</p>}
                <h4 className="text-sm font-bold text-[#1D1D1F]">Quick Review</h4>
                <div className="flex items-baseline gap-2 mt-1 mb-1">
                  <p className="text-xl font-bold text-[#1D1D1F]">{isProPlan ? '₹2,999' : '₹4,999'}</p>
                  {isProPlan && <p className="text-sm text-[#1D1D1F]/30 line-through">₹4,999</p>}
                </div>
                {isProPlan && <p className="text-[10px] text-emerald-600 font-bold mb-2">Full Analysis members save ₹2,000</p>}
                <ul className="space-y-1 mb-4 text-xs text-[#1D1D1F]/60">{['20-30 min call', 'Key risks reviewed', 'Direction correction'].map((t,i) => <li key={i}>• {t}</li>)}</ul>
                <Button data-testid="buy-quick-review-btn" onClick={() => handleConsultation(isProPlan ? 'quick_review_priority' : 'quick_review')} className={`w-full rounded-full py-2.5 text-xs font-semibold ${isProPlan ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E5E5EA]'}`}>Get Quick Review</Button>
              </div>
              <div className="bg-white rounded-2xl border border-[#1D1D1F] p-5">
                <h4 className="text-sm font-bold text-[#1D1D1F]">Strategy Session</h4>
                <p className="text-xl font-bold text-[#1D1D1F] mt-1 mb-3">₹25,000</p>
                <ul className="space-y-1 mb-4 text-xs text-[#1D1D1F]/60">{['60-90 min deep dive', 'Full concept + financial review', 'Business model refinement', 'Clear next steps'].map((t,i) => <li key={i}>• {t}</li>)}</ul>
                <Button data-testid="buy-strategy-session-btn" onClick={() => handleConsultation('strategy_session')} className="w-full bg-[#1D1D1F] text-white hover:bg-[#434344] rounded-full py-2.5 text-xs font-semibold">Book Strategy Session</Button>
              </div>
            </div>
            <p className="text-[10px] text-[#1D1D1F]/30 mt-4 text-center">Led by Rohit Chidurala — MasterChef India Telugu semifinalist</p>
          </section>
        )}

        {/* ─── 10. FINAL VERDICT ─── */}
        <section className={`rounded-3xl p-6 md:p-8 mb-6 border ${SCORE_BG[score?.label]}`} data-testid="final-verdict">
          <h3 className={`text-lg font-bold mb-2 ${SCORE_TEXT[score?.label]}`}>
            Verdict: {score?.label}
          </h3>
          <ul className="space-y-1 mb-4">
            <li className={`text-xs font-medium ${SCORE_TEXT[score?.label]}`}>Score: {score?.total}/100</li>
            <li className={`text-xs font-medium ${SCORE_TEXT[score?.label]}`}>Margin: {financials?.operating_margin}% | Profit: {fc(financials?.monthly_profit)}/mo</li>
            <li className={`text-xs font-medium ${SCORE_TEXT[score?.label]}`}>{risks?.length || 0} risk{risks?.length !== 1 ? 's' : ''} flagged | Data: {confidence?.level} confidence | Execution: {execution_dependency?.level}</li>
          </ul>
          <p className="text-[10px] text-[#1D1D1F]/30 font-medium">This evaluates the business model, not execution quality.</p>
        </section>

        {/* ─── SHARE ─── */}
        <ShareReportBar score={score?.total} label={score?.label} modelLabel={model_label} reportId={reportId} />

        {/* ─── 11. FEEDBACK ─── */}
        <FeedbackSection reportId={reportId} />

      </div>
      <SiteFooter />
    </div>
  );
}
